import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { WizardSteps } from '../components/promotions/wizard/WizardSteps';
import { Step1InfosPromotion } from '../components/promotions/wizard/Step1InfosPromotion';
import { Step2ConfigBlocs } from '../components/promotions/wizard/Step2ConfigBlocs';
import { Step3GenerationLots } from '../components/promotions/wizard/Step3GenerationLots';
import Step4PhotosDocuments from '../components/promotions/wizard/Step4PhotosDocuments';
import Step5Recap from '../components/promotions/wizard/Step5Recap';

const STEPS = [
  { number: 1, name: 'Infos générales' },
  { number: 2, name: 'Blocs' },
  { number: 3, name: 'Lots' },
  { number: 4, name: 'Photos' },
  { number: 5, name: 'Publication' },
];

interface BlockData {
  block_name: string;
  block_order: number;
  total_floors: number;
  elevators_count: number;
  studio_count: number;
  f2_count: number;
  f3_count: number;
  f4_count: number;
  f5_count: number;
  duplex_count: number;
  triplex_count: number;
  commercial_count: number;
}

interface FormData {
  name: string;
  address: string;
  city: string;
  wilaya: string;
  promoter_name: string;
  promoter_phone: string;
  promoter_email: string;
  delivery_date: string;
  construction_progress: number;
  description: string;
  blocks: BlockData[];
  units: any[];
  photos: string[];
  master_plan_url?: string;
  brochure_url?: string;
  typePhotos?: {
    f2: string[];
    f3: string[];
    f4: string[];
    f5: string[];
  };
  status: string;
  is_published: boolean;
  publish_dar_dz: boolean;
  has_collective_pool?: boolean;
  has_private_pool?: boolean;
  has_water_tank?: boolean;
  has_security_cameras?: boolean;
  has_digicode?: boolean;
  has_guardian?: boolean;
  has_intercom?: boolean;
  has_central_heating?: boolean;
  has_central_air_conditioning?: boolean;
  has_individual_parking_box?: boolean;
}

export const EditPromotionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    address: '',
    city: '',
    wilaya: '',
    promoter_name: '',
    promoter_phone: '',
    promoter_email: '',
    delivery_date: '',
    construction_progress: 0,
    description: '',
    blocks: [],
    units: [],
    photos: [],
    master_plan_url: '',
    brochure_url: '',
    typePhotos: {
      f2: [],
      f3: [],
      f4: [],
      f5: [],
    },
    status: 'draft',
    is_published: false,
    publish_dar_dz: false,
    has_collective_pool: false,
    has_private_pool: false,
    has_water_tank: false,
    has_security_cameras: false,
    has_digicode: false,
    has_guardian: false,
    has_intercom: false,
    has_central_heating: false,
    has_central_air_conditioning: false,
    has_individual_parking_box: false,
  });

  useEffect(() => {
    fetchPromotion();
  }, [id]);

  const fetchPromotion = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
      const response = await fetch(`${apiUrl}/promotions/${id}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      setFormData({
        name: data.name || '',
        address: data.address || '',
        city: data.city || '',
        wilaya: data.wilaya || '',
        promoter_name: data.promoter_name || '',
        promoter_phone: data.promoter_phone || '',
        promoter_email: data.promoter_email || '',
        delivery_date: data.delivery_date ? data.delivery_date.split('T')[0] : '',
        construction_progress: data.construction_progress || 0,
        description: data.description || '',
        blocks: data.blocks || [],
        units: [],
        photos: data.photos || [],
        master_plan_url: data.master_plan_url || '',
        brochure_url: data.brochure_url || '',
        typePhotos: data.typePhotos || {
          f2: [],
          f3: [],
          f4: [],
          f5: [],
        },
        status: data.status || 'draft',
        has_collective_pool: Boolean(data.has_collective_pool),
        has_private_pool: Boolean(data.has_private_pool),
        has_water_tank: Boolean(data.has_water_tank),
        has_security_cameras: Boolean(data.has_security_cameras),
        has_digicode: Boolean(data.has_digicode),
        has_guardian: Boolean(data.has_guardian),
        has_intercom: Boolean(data.has_intercom),
        has_central_heating: Boolean(data.has_central_heating),
        has_central_air_conditioning: Boolean(data.has_central_air_conditioning),
        has_individual_parking_box: Boolean(data.has_individual_parking_box),
        is_published: data.is_published || false,
        publish_dar_dz: data.publish_dar_dz || false,
      });
    } catch (error) {
      console.error('Erreur chargement promotion:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const updateFormData = (data: Partial<FormData>) => {
    setFormData({ ...formData, ...data });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Modifier la promotion
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {formData.name}
          </p>
        </div>

        <WizardSteps steps={STEPS} currentStep={currentStep} onStepClick={setCurrentStep} />

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mt-6">
          {currentStep === 1 && (
            <Step1InfosPromotion
              data={formData}
              onUpdate={updateFormData}
              onNext={handleNext}
              onCancel={() => navigate(`/promotions/${id}`)}
              promotionId={id}
            />
          )}

          {currentStep === 2 && (
            <Step2ConfigBlocs
              data={formData}
              onUpdate={updateFormData}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === 3 && (
            <Step3GenerationLots
              data={formData}
              onUpdate={updateFormData}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === 4 && (
            <Step4PhotosDocuments
              data={formData}
              onUpdate={updateFormData}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === 5 && (
            <Step5Recap
              data={formData}
              onBack={handleBack}
              isEdit={true}
              promotionId={id}
            />
          )}
        </div>
      </div>
    </div>
  );
};
